import { inject, Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { concatLatestFrom } from '@ngrx/operators';
import { concatMap, map, Observable, of, tap } from 'rxjs';

import { ApiDef, DatasetInfo } from '@blaze-shared/api-contracts-domain';
import {
  FeatureFlag,
  FeatureFlagsFacade,
  ProjectContextFacade,
} from '@blaze-shared/app-state-data-access';
import { AuthService } from '@blaze-shared/auth-data-access';
import { ConnectorsApiService } from '@blaze-shared/connectors-data-access';
import {
  ApiAction,
  ConnectorConfiguration,
  FormConfig,
  FormValues,
} from '@blaze-shared/connectors-domain';
import { BlazeTable } from '@blaze-shared/datasets-domain';
import { DatasetRow, DatasetSchema } from '@blaze-shared/schema-domain';

import { DialogPage } from '../models/data-connector-dialog';

export interface DataConnectorsState {
  availableApiActions: ApiAction[];
  availableDatasets: DatasetInfo[];
  blazeTable: BlazeTable | null;
  blazeTablePageToken: string | null;
  blazeTableTotalCount: number | null;
  connector: ConnectorConfiguration | null;
  connectorConfigChanged: boolean;
  /** Configured connector from other environment */
  coupledConnector: ConnectorConfiguration | null;
  currentPage: DialogPage;
  customApiDefinition: ApiDef | null;
  formConfig: FormConfig;
  formValues: FormValues;
  loadingDatasets: boolean;
  previewData: DatasetRow[];
  previewDataset: DatasetInfo | null;
  previewTable: BlazeTable | null;
  previouslySelectedDatasets: DatasetSchema[];
  projectId: string;
  selectedApiActions: ApiAction[];
  selectedDatasets: DatasetInfo[];
}

const initialState: DataConnectorsState = {
  availableApiActions: [],
  availableDatasets: [],
  blazeTable: null,
  blazeTablePageToken: null,
  blazeTableTotalCount: null,
  connector: null,
  connectorConfigChanged: false,
  coupledConnector: null,
  currentPage: DialogPage.Undetermined,
  customApiDefinition: null,
  formConfig: [],
  formValues: {},
  loadingDatasets: false,
  previewData: [],
  previewDataset: null,
  previewTable: null,
  previouslySelectedDatasets: [],
  projectId: '',
  selectedApiActions: [],
  selectedDatasets: [],
};

@Injectable()
export class DataConnectorsStore extends ComponentStore<DataConnectorsState> {
  private authService = inject(AuthService);
  private connectorsApiService = inject(ConnectorsApiService);
  private featureFlagsFacade = inject(FeatureFlagsFacade);
  private projectContextFacade = inject(ProjectContextFacade);

  // Read
  readonly availableApiActions$: Observable<ApiAction[]> = this.select(
    (state) => state.availableApiActions,
  );
  readonly availableDatasets$: Observable<DatasetInfo[]> = this.select(
    (state) => state.availableDatasets,
  );
  readonly blazeTable$: Observable<BlazeTable | null> = this.select((state) => state.blazeTable);
  readonly blazeTablePageToken$: Observable<string | null> = this.select(
    (state) => state.blazeTablePageToken,
  );
  readonly blazeTableTotalCount$: Observable<number | null> = this.select(
    (state) => state.blazeTableTotalCount,
  );
  readonly currentPage$: Observable<DialogPage> = this.select((state) => state.currentPage);
  readonly customApiDefinition$: Observable<ApiDef | null> = this.select(
    (state) => state.customApiDefinition,
  );
  readonly connector$: Observable<ConnectorConfiguration | null> = this.select(
    (state) => state.connector,
  );
  readonly connectorConfigChanged$: Observable<boolean> = this.select(
    (state) => state.connectorConfigChanged,
  );
  readonly coupledConnector$: Observable<ConnectorConfiguration | null> = this.select(
    (state) => state.coupledConnector,
  );
  readonly formConfig$: Observable<FormConfig> = this.select((state) => state.formConfig);
  readonly formValues$: Observable<FormValues> = this.select((state) => state.formValues);
  readonly loadingDatasets$: Observable<boolean> = this.select((state) => state.loadingDatasets);
  readonly previewData$: Observable<DatasetRow[]> = this.select((state) => state.previewData);
  readonly previewDataset$: Observable<DatasetInfo | null> = this.select(
    (state) => state.previewDataset,
  );
  readonly previewTable$: Observable<BlazeTable | null> = this.select(
    (state) => state.previewTable,
  );
  readonly previouslySelectedDatasets$: Observable<DatasetSchema[]> = this.select(
    (state) => state.previouslySelectedDatasets,
  );
  readonly projectId$: Observable<string> = this.select((state) => state.projectId);
  readonly selectedApiActions$: Observable<ApiAction[]> = this.select(
    (state) => state.selectedApiActions,
  );
  readonly selectedDatasets$: Observable<DatasetInfo[]> = this.select(
    (state) => state.selectedDatasets,
  );

  // Set
  readonly setAvailableApiActions = this.updater((state, apiActions: ApiAction[]) => ({
    ...state,
    availableApiActions: [...apiActions],
  }));
  readonly setAvailableDatasets = this.updater((state, datasets: DatasetInfo[]) => ({
    ...state,
    availableDatasets: [...datasets],
  }));
  readonly setBlazeTable = this.updater((state, table: BlazeTable) => ({
    ...state,
    blazeTable: { ...table },
  }));
  readonly setBlazeTablePageToken = this.updater((state, pageToken: string | null) => ({
    ...state,
    blazeTablePageToken: pageToken,
  }));
  readonly setBlazeTableTotalCount = this.updater((state, totalCount: number | null) => ({
    ...state,
    blazeTableTotalCount: totalCount,
  }));
  readonly setCurrentPage = this.updater((state, page: DialogPage) => ({
    ...state,
    currentPage: page,
  }));
  readonly setConnectorConfigurationChanged = this.updater((state, value: boolean) => ({
    ...state,
    connectorConfigChanged: value,
  }));
  readonly setFormConfig = this.updater((state, config: FormConfig) => ({
    ...state,
    formConfig: [...config],
  }));
  readonly setFormValues = this.updater((state, values: FormValues) => ({
    ...state,
    formValues: { ...values },
  }));
  readonly setLoadingDatasets = this.updater((state, value: boolean) => ({
    ...state,
    loadingDatasets: value,
  }));
  readonly setPreviewData = this.updater((state, previewData: DatasetRow[]) => ({
    ...state,
    previewData: [...previewData],
  }));
  readonly setProjectId = this.updater((state, projectId: string) => ({
    ...state,
    projectId,
  }));
  readonly setPreviewDataset = this.updater((state, dataset: DatasetInfo) => ({
    ...state,
    previewDataset: { ...dataset },
  }));
  readonly setPreviewTable = this.updater((state, table: BlazeTable) => ({
    ...state,
    previewTable: { ...table },
  }));
  readonly setPreviouslySelectedDatasets = this.updater((state, datasets: DatasetSchema[]) => ({
    ...state,
    previouslySelectedDatasets: [...datasets],
  }));
  readonly setSelectedApiActions = this.updater((state, apiActions: ApiAction[]) => ({
    ...state,
    selectedApiActions: [...apiActions],
  }));
  readonly setSelectedDatasets = this.updater((state, datasets: DatasetInfo[]) => ({
    ...state,
    selectedDatasets: [...datasets],
  }));
  readonly setCustomApiDefinition = this.updater(
    (state, definition: ApiDef | null | undefined) => ({
      ...state,
      customApiDefinition: definition ? { ...definition } : null,
    }),
  );
  readonly setConnector = this.updater((state, connector: ConnectorConfiguration | null) => ({
    ...state,
    connector,
  }));

  // Add
  addSelectedDataset = this.updater((state, dataset: DatasetInfo) => ({
    ...state,
    selectedDatasets: [...state.selectedDatasets, dataset],
  }));

  // Delete
  readonly destroy = this.updater(() => ({ ...initialState }));

  readonly deleteSelectedDataset = this.updater((state, dataset: DatasetInfo) => {
    const selectedDatasets = [...state.selectedDatasets];
    const datasetIndex = selectedDatasets?.findIndex(
      (selectedDataset) => selectedDataset?.name === dataset?.name,
    );
    if (datasetIndex >= 0) {
      selectedDatasets.splice(datasetIndex, 1);
    }
    return { ...state, selectedDatasets };
  });

  readonly deleteAvailableDatasets = this.updater((state) => ({
    ...state,
    availableDatasets: [],
  }));

  constructor() {
    super(initialState);

    this.effect(() =>
      this.connector$.pipe(
        map((connector) => connector?.id ?? ''),
        concatLatestFrom(() => [
          this.featureFlagsFacade
            .isEnabled(FeatureFlag.MultipleEnvironments, false)
            .pipe(map((isEnabled) => isEnabled && this.authService.isCurrentUserAccountAdmin())),
          this.projectContextFacade.environmentName$.pipe(
            map((environment) => {
              if (environment === 'Production') {
                return 'Development';
              } else if (environment === 'Development') {
                return 'Production';
              } else {
                return undefined;
              }
            }),
          ),
        ]),
        concatMap(([connectorId, isEnabled, requestedEnvironmentName]) => {
          if (!connectorId || !isEnabled || !requestedEnvironmentName) {
            return of(null);
          }
          return this.connectorsApiService.getConnector(connectorId, requestedEnvironmentName);
        }),
        tap((connector) => {
          this.patchState({ coupledConnector: connector || null });
        }),
      ),
    );
  }
}
